import type { MouseEvent, ReactNode } from 'react'
import { motion, Transition, Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { Tooltip } from '@highlight/ui'
import { FLOATING_SURFACE_BORDER_CLASS, FLOATING_SURFACE_STYLE } from '@renderer/components/floating-surface'

interface SidepanelActionProps {
  icon: ReactNode
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  className?: string
  tooltip?: string
  'aria-label'?: string
  /** Set for toggle buttons, so the pressed state carries without a visual-only cue. */
  'aria-pressed'?: boolean
}

const actionVariants: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
}

const actionTransition: Transition = {
  type: 'spring',
  duration: 0.1,
  bounce: 0.05,
}

export function SidepanelCircularButton({
  icon,
  onClick,
  tooltip,
  className,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
}: SidepanelActionProps) {
  const button = (
    <motion.button
      variants={actionVariants}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
      transition={actionTransition}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      className={twMerge(
        'flex size-10 cursor-pointer items-center justify-center rounded-full',
        FLOATING_SURFACE_BORDER_CLASS,
        className,
      )}
      style={FLOATING_SURFACE_STYLE}
    >
      {icon}
    </motion.button>
  )

  return (
    <Tooltip disabled={!tooltip}>
      <Tooltip.Trigger render={button} />
      <Tooltip.Content>{tooltip}</Tooltip.Content>
    </Tooltip>
  )
}
